/**
 * @file JS - Controller - LineVectorLayerController.js
 * @author Adam Lemire
 * @since 2020/07/23
 * @version 1.0
 * @summary Dernières modifications : 2021/12/15
 */

import { LayerProperties } from '../../../helpers/Selectors.js';
import { ClassificationType, ColorHexList, ColorPalettes, GradationType, StyleTypes } from '../../../helpers/Constants.js';
import { getRandomRGBAColor } from '../../../helpers/GlobalFunctions.js';
import VectorLayer from '../../../classes/Layer/VectorLayer.js'; // eslint-disable-line no-unused-vars
import VectorLayerStyle from '../../../classes/Layer/VectorLayerStyle.js'; // eslint-disable-line no-unused-vars

/** Controller devant être adapté par VectorLayerAdapter */
export default class LineVectorLayerController {
  static createSymbolPreviewDiv () {
    const symbol = (parseInt($(LayerProperties.LAYER_STROKE_WIDTH).val())) > 1
      ? '<div class="symbol-preview__content" style="' +
      'width: 25%; ' +
      'height:' + (parseInt($(LayerProperties.LAYER_STROKE_WIDTH).val())) + 'px; ' +
      'background:' + $(LayerProperties.BUTTON.STROKE_COLOR).css('background-color') + '; ' +
      'margin: 0px 0px 0px 20px; ' +
      'position: absolute; ' +
      'top: 50%; ' +
      'MsTransform: translateY(-50%); ' +
      'transform: translateY(-50%); ' +
      '"></div>'
      : '<div class="symbol-preview__content" style="' +
      'width: 25%; ' +
      'height:' + (parseInt($(LayerProperties.LAYER_STROKE_WIDTH).val())) + 'px; ' +
      'background:' + $(LayerProperties.BUTTON.STROKE_COLOR).css('background-color') + '; ' +
      'margin: 0px 0px 0px 20px; ' +
      'position: absolute; ' +
      'top: 50%; ' +
      '"></div>';

    return symbol;
  }

  static creerDivApercuSymboleAutres () {
    const symbol = '<div class="symbol-preview__content" style="' +
      'width: 25%; ' +
      'height:' + (parseInt($(LayerProperties.SYMBOLE_AUTRES_CONTOUR_EPAISSEUR).val())) + 'px; ' +
      'background:' + $(LayerProperties.SYMBOLE_AUTRES_CONTOUR_COULEUR).css('background-color') + '; ' +
      'margin: 0px 0px 0px 20px; ' +
      'position: absolute; ' +
      'top: 50%; ' +
      'MsTransform: translateY(-50%); ' +
      'transform: translateY(-50%); ' +
      '"></div>';

    return symbol;
  }

  static showFeatureStyleProperties () {
    $(LayerProperties.FILL.COLOR).hide();
    $(LayerProperties.FILL.COLOR_LABEL).hide();
    $(LayerProperties.FILL.TITLE_LABEL).hide();
    $(LayerProperties.LAYER_STROKE_COLOR).show();
    $(LayerProperties.LAYER_STROKE_WIDTH).show();
    $(LayerProperties.LABEL_SYMBOL_SIZE).addClass('hidden');
    $(LayerProperties.LAYER_SYMBOL_SIZE).addClass('hidden');
    $(LayerProperties.SYMBOLE_AUTRES_TAILLE).hide();
    $(LayerProperties.SYMBOLE_AUTRES_TAILLE_ETIQUETTE).hide();
  }

  /*
   * Groupes
   */

  /**
   * @param { Map < number, GroupStyle > } symbolStyles
   */
  static createGroupsPropertiesRows (symbolStyles) {
    $('.advanced-preview__row').remove();
    symbolStyles.forEach(function (symbolStyle, index) {
      const row = `
      <tr class="advanced-preview__row" data-group="` + symbolStyle.groupValue + `">
        <td class="advanced-preview__symbol--` + symbolStyle.id + '" style="display: block; position:relative; height:30px"><div class="symbol-preview__content symbol-preview__content--line symbol-preview__content--' + symbolStyle.id + `">
        </div></td>
        <td class="advanced-preview__group">` + symbolStyle.groupValueLabel + `</td>
        <td class="advanced-preview__label"><input type="text" id="advanced-preview__text--` + symbolStyle.id + '" class="advanced-preview__text" value="' + symbolStyle.groupLabel + `"></td>
        <td class="advanced-preview__counter type-graduated">` + symbolStyle.count + `</td>
      </tr>`;

      $(LayerProperties.ADVANCED_PREVIEW.CONTAINER).append(row);
      LineVectorLayerController.createGroupSymbolPreviewDiv(symbolStyle);
    });
  }

  /**
   * @param { VectorLayer } layer
   * @returns { Map < number, GroupStyle > }
   */
  static getNewSymbolStyles (layer) {
    const symbolStyles = $(LayerProperties.SYMBOL_STYLE_TYPE).val() === StyleTypes.CATEGORIZED
      ? LineVectorLayerController.getNewCategorizedSymboleStyles(layer)
      : LineVectorLayerController.getNewGraduatedSymbolStyles(layer);
    return symbolStyles;
  }

  /**
   * @param { VectorLayer } layer
   * @returns { Map < number, GroupStyle > }
   */
  static getNewCategorizedSymboleStyles (layer) {
    const groups = layer.obtenirValeursDistinctes($(LayerProperties.GROUP_VALUE_SELECTOR).val());
    const radius = 6;
    const strokeWidth = groups.length < 31 ? 2 : 1;
    const symbolStyles = new Map();

    groups.forEach(function (group, index) {
      const randomColor = index < 30 ? 0 : getRandomRGBAColor();
      const backgroundColor = index < 10
        ? ColorHexList.BRIGHT[index]
        : index < 20
          ? ColorHexList.LIGHT[index - 10]
          : index < 30
            ? ColorHexList.DARK[index - 20]
            : randomColor;
      const strokeColor = index < 10
        ? ColorHexList.DARK[index]
        : index < 20
          ? ColorHexList.BRIGHT[index - 10]
          : index < 30
            ? ColorHexList.LIGHT[index - 20]
            : randomColor;
      const symbolStyle = {
        id: index,
        fillColor: backgroundColor,
        strokeColor: strokeColor,
        strokeWidth: strokeWidth,
        groupLabel: group,
        groupValueLabel: group,
        groupValue: group,
        radius: radius
      };
      symbolStyles.set(group, symbolStyle);
    });
    return symbolStyles;
  }

  /**
   * @param { object } classification
   * @returns { Map < number, GroupStyle > }
   */
  static getNewGraduatedSymbolStyles (classification) {
    const radius = 1;
    const symbolStyles = new Map();
    const precision = $(LayerProperties.PRECISION_SELECTOR).val();
    const counter = classification.groups.counter;

    try {
      if (classification.type === ClassificationType.ATTRIBUTE) {
        const groups = classification.groups.bounds || [0];
        groups.sort(function (a, b) { return a - b; });

        const max = classification.groups.max();

        groups.forEach(function (group, index) {
          let strokeColor;
          let strokeWidth;
          const groupNumber = isNaN(group) ? 0 : group;
          const colorPalette = ColorPalettes[$(LayerProperties.COLOR_GRADIENT_SELECTOR).val()];
          const backgroundColor = 'black';

          if ($('.gradation-type input:checked').val() === GradationType.COLOR) {
            strokeColor = colorPalette(1 - group / (max));
            strokeWidth = 2;
          } else {
            strokeColor = colorPalette(0);
            strokeWidth = Math.ceil((groups.length < 12 ? groups.length : 12) * ((groupNumber + 1) / (max + 1)));
          }

          /** @type { GroupStyle } */
          const symbolStyle = {
            id: index,
            count: counter[index],
            fillColor: backgroundColor,
            strokeColor: strokeColor,
            strokeWidth: strokeWidth,
            groupLabel: group.toFixed(precision),
            groupValueLabel: group.toFixed(precision),
            groupValue: group,
            radius: radius
          };
          symbolStyles.set(group, symbolStyle);
        });
      } else {
        const ranges = classification.groups.ranges;

        ranges.forEach(function (groupLabel, index) {
          let strokeColor;
          let strokeWidth;
          const groupNumber = index;
          const colorPalette = ColorPalettes[$(LayerProperties.COLOR_GRADIENT_SELECTOR).val()];
          const backgroundColor = 'black';

          if (classification.type === ClassificationType.STD_DEVIATION) {
            strokeColor = ColorHexList.COLD_HOT[index];
            strokeWidth = 2;
          } else {
            if ($('.gradation-type input:checked').val() === GradationType.COLOR) {
              strokeColor = colorPalette(1 - index / (ranges.length - 1));
              strokeWidth = 2;
            } else {
              strokeColor = colorPalette(0);
              strokeWidth = groupNumber + 1;
            }
          }

          /** @type { GroupStyle } */
          const symbolStyle = {
            id: index,
            count: counter[index],
            fillColor: backgroundColor,
            strokeColor: strokeColor,
            strokeWidth: strokeWidth,
            groupLabel: groupLabel,
            groupValueLabel: groupLabel,
            groupValue: groupNumber,
            radius: radius
          };
          symbolStyles.set(groupNumber, symbolStyle);
        });
      }
      return symbolStyles;
    } catch (err) {
      console.log(err.message);
    }
  }

  /**
   * @param { GroupStyle } groupStyle
   */
  static createGroupSymbolPreviewDiv (groupStyle) {
    const div = '.symbol-preview__content--' + groupStyle.id;

    const symbol = (groupStyle.strokeWidth) > 1
      ? '<div style="' +
      'width: 25%; ' +
      'height: ' + groupStyle.strokeWidth + 'px; ' +
      'background: ' + groupStyle.strokeColor + '; ' +
      'margin: 0px 0px 0px 0px; ' +
      'position: absolute; ' +
      'top: 50%; ' +
      'MsTransform: translateY(-50%); ' +
      'transform: translateY(-50%); ' +
      '"></div>'
      : '<div style="' +
      'width: 25%; ' +
      'height: 1px; ' +
      'background: ' + groupStyle.strokeColor + '; ' +
      'margin: 0px 0px 0px 0px; ' +
      'position: absolute; ' +
      'top: 50%; ' +
      '"></div>';

    $(div).append(symbol);
  }

  /**
   * @param { GroupStyle } advancedStyleObject
   */
  static updatePropertyEditorValues (advancedStyleObject) {
    $(LayerProperties.BUTTON.STROKE_COLOR).css('background-color', advancedStyleObject.strokeColor);
    $(LayerProperties.LAYER_STROKE_WIDTH).val(advancedStyleObject.strokeWidth);
    $(LayerProperties.SYMBOL_STYLE_GROUP_LABEL).html(advancedStyleObject.groupLabel);
    $(LayerProperties.SYMBOL_STYLE_GROUP_LABEL).data('group', advancedStyleObject.groupValue);
  }

  /**
   * @param { VectorLayerStyle } vectorLayerStyle
   */
  static updatePropertyEditorGroupSymbol (vectorLayerStyle) {
    if ($(LayerProperties.SYMBOL_STYLE_TYPE).val() !== StyleTypes.SINGLE) {
      const group = $(LayerProperties.SYMBOL_STYLE_GROUP_LABEL).data('group');
      const groupId = vectorLayerStyle.symbolStyles.get(group).id;
      const div = '.symbol-preview__content--' + groupId + ' div';

      $(div).css('height', $(LayerProperties.LAYER_STROKE_WIDTH).val() + 'px');
      $(div).css('background-color', $(LayerProperties.BUTTON.STROKE_COLOR).css('background-color'));

      vectorLayerStyle.symbolStyles.get(group).strokeColor = $(LayerProperties.BUTTON.STROKE_COLOR).css('background-color');
      vectorLayerStyle.symbolStyles.get(group).strokeWidth = $(LayerProperties.LAYER_STROKE_WIDTH).val();
    }
  }

  /*
  * Légende
  */

  /**
   * @param { object } symbolStyle
   * @returns { string }
   */
  static createSingleLegendSymbolDiv (symbolStyle) {
    const symbol = (symbolStyle.strokeWidth) > 1
      ? '<div class="legend__single-symbol legend__single-symbol--line"><div style="' +
      'width: 100%; ' +
      'height:' + symbolStyle.strokeWidth + 'px; ' +
      'background:' + symbolStyle.strokeColor + '; ' +
      'margin: 0px 0px 0px 0px; ' +
      'position: relative; ' +
      'top: 50%; ' +
      'MsTransform: translateY(-50%); ' +
      'transform: translateY(-50%); ' +
      '"></div></div>'
      : '<div class="legend__single-symbol legend__single-symbol--line"><div style="' +
      'width: 100%; ' +
      'height:' + symbolStyle.strokeWidth + 'px; ' +
      'background:' + symbolStyle.strokeColor + '; ' +
      'margin: 0px 0px 0px 0px; ' +
      'position: relative; ' +
      'top: 50%; ' +
      '"></div></div>';

    return symbol;
  }

  /**
   * @param { Map } symbolStyles
   * @returns {string}
   */
  static createMultiLegendSymbolsDiv (symbolStyles) {
    let divs = '<table class="legend__multi-styles">';
    symbolStyles.forEach(function (value, key) {
      const div = (value.strokeWidth) > 1
        ? '<tr class="legend__group-container"><td class="legend__group-symbol legend__group-symbol--line"><div style="' +
        'width: 100%; ' +
        'height:' + value.strokeWidth + 'px; ' +
        'background:' + value.strokeColor + '; ' +
        'margin: 0px 0px 0px 0px; ' +
        'position: absolute; ' +
        'top: 50%; ' +
        'MsTransform: translateY(-50%); ' +
        'transform: translateY(-50%); ' +
        '"></div></td><td class="legend__group-label">' + value.groupLabel + '</td></div>'
        : '<tr class="legend__group-container"><td class="legend__group-symbol legend__group-symbol--line"><div style="' +
        'width: 100%; ' +
        'height:' + value.strokeWidth + 'px; ' +
        'background:' + value.strokeColor + '; ' +
        'margin: 0px 0px 0px 0px; ' +
        'position: absolute; ' +
        'top: 50%; ' +
        '"></div></td><td class="legend__group-label">' + value.groupLabel + '</td></tr>';
      divs += div;
    });
    divs += '</table>';
    return divs;
  }
}
